import { Circle, CircleCheck, CircleDot, Plus } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { cn } from "@/app/lib/utils";
import { getRailSection } from "@/app/router/resumeRoutes";

import { NavigationHandler } from "../../../handlers/navigationHandler";

/** One row in the progress rail, computed by ResumeBuilderLayout from the draft. */
export interface ResumeSection {
	section: string;
	title: string;
	progressStatus: string;
	noClick?: boolean;
	isExtraSection?: boolean;
}

interface ResumeProgressProps {
	resumeSections: ResumeSection[];
}

/**
 * Left progress sidebar. Hidden below md, a 72px icon rail on md, and the full
 * 260px sidebar from lg (mirrors the `ml-18 lg:ml-[260px]` offset in the layout).
 */
const ResumeProgress = ({ resumeSections }: ResumeProgressProps) => {
	const navigationHandler = new NavigationHandler();
	const navigate = useNavigate();
	const location = useLocation();

	function navigateToStep(link: string) {
		navigationHandler.setFinalize(link);
		navigate(`/resume/${link}`);
	}

	const currentSection = location.pathname.split("/")[2] ?? "";

	// Resolve the route to the row that owns it, then match rows by identity.
	// The previous `pathname.includes(item.section)` matched nothing on the
	// sub-routes (work-history, work-details, education-information), so the rail
	// went blank exactly where the user needed to see which step they were on.
	const activeSection = getRailSection(currentSection);

	// `custom-extra/:name` is stored as a single section string — compare its head.
	const isRowActive = (section: string) => section.split("/")[0] === activeSection;

	const extrasSections = resumeSections.filter((item) => item.isExtraSection === true);
	const showAddAnotherSection = extrasSections.some((item) => isRowActive(item.section)) || currentSection === "extras";

	// The rail colour is repeated by each icon's mask span below — keep them in step.
	return (
		<aside className="fixed top-0 left-0 z-40 hidden h-screen w-18 flex-col bg-[#f8f8f8] md:flex lg:w-[260px]">
			<div className="flex h-full flex-col overflow-y-auto pb-10">
				<Link to="/" className="hidden shrink-0 p-4 lg:block">
					<img src="/images/logo.png" alt="Dproz" className="max-w-[150px]" />
				</Link>

				<div className="relative">
					{resumeSections.map((item, index) => {
						const isActive = isRowActive(item.section);
						const isCompleted = item.progressStatus === "COMPLETED";
						const isFirst = index === 0;
						const isLast = index === resumeSections.length - 1;

						return (
							<button
								key={`${item.section}-${index}`}
								type="button"
								aria-current={isActive ? "step" : undefined}
								onClick={() => navigateToStep(item.section)}
								className={cn(
									"relative flex w-full cursor-pointer items-center justify-center p-4 text-left lg:justify-start rounded-none",
									isActive &&
										"bg-white before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-primary-600",
								)}
							>
								{/* Connector drawn per row rather than once for the whole list, so it
								    tracks the icon centre at both breakpoints and however tall a row
								    grows. `p-4` is symmetric, so the icon sits at 50% of the row on
								    both axes on the rail; from lg the row is left-aligned and the
								    centre is pl-4 + half of size-5. */}
								{!(isFirst && isLast) && (
									<span
										aria-hidden="true"
										className={cn(
											"absolute left-1/2 z-0 w-px -translate-x-1/2 bg-[#d6e7ff] lg:left-[26px]",
											isFirst ? "top-1/2 bottom-0" : isLast ? "top-0 bottom-1/2" : "inset-y-0",
										)}
									/>
								)}
								<span className={cn("relative z-10 rounded-full", isActive ? "bg-white" : "bg-[#f8f8f8]")}>
									{/* Completion wins over "you are here": a finished step the user has
									    navigated back to still reads as done. */}
									{isCompleted ? (
										<CircleCheck className="size-5 fill-success-default stroke-white" />
									) : isActive ? (
										<CircleDot className="size-5 text-primary-600" />
									) : (
										<Circle className="size-5 text-[#d6e7ff]" />
									)}
								</span>
								<span
									className={cn(
										"relative z-10 ml-2 hidden font-semibold text-sm leading-none lg:inline",
										isActive ? "text-[#161616]" : isCompleted ? "text-[#4a4a4a]" : "text-[#7a7a7a]",
									)}
								>
									{item.title}
								</span>
							</button>
						);
					})}

					{showAddAnotherSection && (
						<button
							type="button"
							onClick={() => navigateToStep("extras")}
							className="relative z-10 flex w-full cursor-pointer items-center justify-center px-4 py-2 font-semibold text-primary-700 lg:justify-start"
						>
							<Plus className="size-4 lg:mr-2" />
							<span className="hidden text-sm lg:inline">Add another section</span>
						</button>
					)}
				</div>
			</div>
		</aside>
	);
};

export default ResumeProgress;
